import { useState } from 'react';
import { archiveApi, type ArchiveSettingsChange, type ArchiveTestStep } from '../../api/client';
import { Button, Card, Chip, Field, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Archive (Storage A0, 1.13.0, Teampace 3085; help: staff-archive): where
 * Stitch's own archive copy goes (a private S3-compatible bucket; for Backblaze B2, its S3 address),
 * tested before it's used. The secret is sealed with DATA_ENCRYPTION_KEY and never shown again. Below:
 * what the stitch-archive container has done, and what waits. "Use Stitch Block Storage's key" (3158)
 * seals a copy of the portal's own B2 key into these settings, so nothing is pasted.
 */
export function ArchiveCard() {
  const view = useData(() => archiveApi.view(), [], { live: false });
  const [form, setForm] = useState<ArchiveSettingsChange>({});
  const [busy, setBusy] = useState('');
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit' | 'warn'; text: string } | null>(null);
  const [steps, setSteps] = useState<ArchiveTestStep[] | null>(null);
  const v = view.data;

  async function run(what: string, fn: () => Promise<string>) {
    setBusy(what);
    setSaid(null);
    try {
      const text = await fn();
      view.reload();
      setSaid({ tone: 'ok', text });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  }
  const field = (k: keyof ArchiveSettingsChange, fallback: string) => (form[k] !== undefined ? (form[k] ?? '') : fallback);
  const set = (k: keyof ArchiveSettingsChange, value: string) => setForm((f) => ({ ...f, [k]: value }));
  const changed = Object.keys(form).length > 0;
  const s = v?.status;

  return (
    <Card title="Archive" action={v ? <Chip tone={v.ready ? (v.lastTest && !v.lastTest.ok ? 'crit' : 'ok') : 'warn'}>{v.ready ? (v.lastTest && !v.lastTest.ok ? 'Test failed' : 'Ready') : 'Not set up'}</Chip> : undefined}>
      <div className="card-body stack">
        <p className="muted small">
          Every company’s calls (and, as they’re added, recordings, voicemail, messages and Patches) are written as open Parquet files to Stitch’s own archive bucket, by the
          stitch-archive container. Use a private bucket of its own. For Backblaze B2, use the bucket’s S3 address (like https://s3.us-east-005.backblazeb2.com) and an
          application key for that bucket only.
        </p>
        {s && (
          <dl className="facts">
            <dt>Waiting</dt>
            <dd>
              {s.waiting.toLocaleString('en-US')}
              {s.oldestWaiting ? ` (oldest from ${shortWhen(s.oldestWaiting)})` : ''}
            </dd>
            <dt>Last hour</dt>
            <dd>
              {s.rowsLastHour.toLocaleString('en-US')} items in {s.partsLastHour} files
            </dd>
            <dt>Last written</dt>
            <dd>{s.lastPartAt ? shortWhen(s.lastPartAt) : 'Nothing yet'}</dd>
            <dt>Archive service</dt>
            <dd>
              {v?.service.ok ? `Running${v.service.version ? ` (${v.service.version})` : ''} at ${v.service.url}` : 'Not answering: History can’t reach older days, and companies’ own storage isn’t copied'}
              {v?.service.ok && !v.service.queryKey ? '. DATA_ENCRYPTION_KEY isn’t set here, so the portal can’t search it.' : ''}
            </dd>
            {s.parked > 0 && (
              <>
                <dt>Stuck</dt>
                <dd>{s.parked.toLocaleString('en-US')} items stopped after a day of trying</dd>
              </>
            )}
          </dl>
        )}
        {v?.blockStorage.available && (
          <div className="stack">
            {v.blockStorage.inUse ? (
              <Notice tone="ok">Using a copy of Stitch Block Storage’s B2 key (sealed). Nothing to paste.</Notice>
            ) : v.blockStorage.stale ? (
              <Notice tone="warn">Stitch Block Storage’s key was changed since the archive copied it. Copy it again, or the archive keeps the old key.</Notice>
            ) : null}
            <div className="row-actions">
              <Button
                variant={v.ready ? 'ghost' : 'primary'}
                disabled={Boolean(busy)}
                title="Seal a copy of the B2 key Stitch Block Storage already uses into the archive's settings, fill in B2's address and region, then test"
                onClick={() =>
                  void run('reuse', async () => {
                    const r = await archiveApi.useBlockStorageKey(form.bucket ? { bucket: form.bucket } : {});
                    setForm({});
                    if (r.test) setSteps(r.test.steps);
                    if (r.test && !r.test.ok) throw new Error('The key was copied, but the test didn’t pass. See the steps below.');
                    const warned = r.test?.steps.find((x) => x.warn);
                    return [r.note, warned ? `Passed, with a warning: ${warned.said}` : `Every step passed on ${r.view.bucket}.`].filter(Boolean).join(' ');
                  })
                }
              >
                {v.blockStorage.inUse || v.blockStorage.stale ? 'Copy Block Storage’s key again' : 'Use Stitch Block Storage’s key'}
              </Button>
            </div>
          </div>
        )}
        {v && !v.ready && v.missing.length > 0 && <Notice tone="warn">Still needed: {v.missing.join(', ')}.</Notice>}
        {v?.lastTest && (
          <Notice tone={v.lastTest.ok ? (v.lastTest.said.startsWith('Passed, with a warning') ? 'warn' : 'ok') : 'crit'} title={`Tested ${shortWhen(v.lastTest.at)}`}>
            {v.lastTest.said}
          </Notice>
        )}
        {v && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void run('save', async () => {
                await archiveApi.save(Object.fromEntries(Object.entries(form).map(([k, val]) => [k, typeof val === 'string' && !val.trim() ? null : val])) as ArchiveSettingsChange);
                setForm({});
                return 'Saved in Stitch. Press Test before relying on it.';
              });
            }}
          >
            <Field label="Address (S3 endpoint)" placeholder="https://s3.us-east-005.backblazeb2.com" value={field('endpoint', v.endpoint)} onChange={(e) => set('endpoint', e.target.value)} spellCheck={false} />
            <Field label="Region" placeholder="us-east-005" value={field('region', v.region)} onChange={(e) => set('region', e.target.value)} spellCheck={false} />
            <Field label="Bucket" placeholder="stitch-archive" value={field('bucket', v.bucket)} onChange={(e) => set('bucket', e.target.value)} spellCheck={false} />
            <Field label="Folder in the bucket (optional)" value={field('prefix', v.prefix)} onChange={(e) => set('prefix', e.target.value)} spellCheck={false} />
            <Field
              label="Archive service address (optional)"
              placeholder="http://stitch_stitch-archive:4200"
              value={field('serviceUrl', v.service.savedUrl)}
              onChange={(e) => set('serviceUrl', e.target.value)}
              spellCheck={false}
              hint="Where the portal reaches the stitch-archive container. Leave empty: Stitch looks for it by its EasyPanel name."
            />
            <Field
              label={v.keyIdEnd ? `New key ID (saved one ends …${v.keyIdEnd})` : 'Key ID'}
              value={form.keyId ?? ''}
              onChange={(e) => set('keyId', e.target.value)}
              autoComplete="off"
              spellCheck={false}
              hint={v.keyIdEnd ? 'Leave empty to keep the one saved.' : undefined}
            />
            <Field
              label={v.secretSet ? 'New secret key' : 'Secret key'}
              type="password"
              value={form.secret ?? ''}
              onChange={(e) => set('secret', e.target.value)}
              autoComplete="new-password"
              hint={v.secretSet ? 'Saved (sealed: never shown again). Leave empty to keep it.' : undefined}
            />
            <div className="row-actions">
              <Button type="submit" variant="primary" disabled={Boolean(busy) || !changed}>
                Save
              </Button>
              <Button
                variant="ghost"
                disabled={Boolean(busy) || !v.ready}
                onClick={() =>
                  void run('test', async () => {
                    const r = await archiveApi.test();
                    setSteps(r.steps);
                    if (!r.ok) throw new Error('The test didn’t pass. See the steps below.');
                    const warned = r.steps.find((x) => x.warn);
                    if (warned) return `Passed, with a warning: ${warned.said}`;
                    return 'Every step passed: Stitch can write, read back, list, read part of a file and delete there, and the bucket isn’t public.';
                  })
                }
              >
                Test
              </Button>
              <Button
                variant="ghost"
                disabled={Boolean(busy) || !v.ready}
                title="Queue every call Stitch already keeps, so the archive starts with them"
                onClick={() =>
                  void run('backfill', async () => {
                    const r = await archiveApi.backfill();
                    const n = (x: number) => x.toLocaleString('en-US');
                    return `Queued for the archive: ${n(r.calls)} calls, ${n(r.messages)} messages, ${n(r.patches)} Patches, ${n(r.meetings ?? 0)} meetings and ${n(r.transcripts)} transcripts.`;
                  })
                }
              >
                Archive what Stitch already keeps
              </Button>
              {(s?.parked ?? 0) > 0 && (
                <Button variant="ghost" disabled={Boolean(busy)} onClick={() => void run('retry', async () => `${(await archiveApi.retry()).retried.toLocaleString('en-US')} items back in line.`)}>
                  Try stuck items again
                </Button>
              )}
            </div>
          </form>
        )}
        {steps && (
          <ul className="reg-list">
            {steps.map((x) => (
              <li key={x.step}>
                <strong>{!x.ok ? 'Failed' : x.warn ? 'Check' : 'OK'}:</strong> {x.step}. {x.said}
              </li>
            ))}
          </ul>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
        {v?.savedAt && (
          <p className="muted small-print">
            Saved {shortWhen(v.savedAt)}
            {v.savedBy ? ` by ${v.savedBy}` : ''}.
          </p>
        )}
      </div>
    </Card>
  );
}
